import { api } from "api";
import { useSyncExternalStore } from "react";
import { z } from "zod";

type T_Base = { _id: string; email?: string; phone?: string; key: string };
type T_D = T_Base & { _meta?: { isProcessing?: boolean; isError?: boolean } };

let isProcessing = false;
let queue: T_D[] = [];
const listeners = new Set<() => void>();

export function splitQueueByStatus(queue: T_D[]) {
  const erroredQ: T_D[] = [];
  const notErroredQ: T_D[] = [];
  queue.forEach((el) => {
    if (el?._meta) {
      if (el?._meta?.isError === false) {
        notErroredQ.push(el);
      } else if (el?._meta?.isError === true) {
        erroredQ.push(el);
      } else {
        notErroredQ.push(el);
      }
    } else {
      notErroredQ.push(el);
    }
  });
  return { erroredQ, notErroredQ };
}
// Process API calls sequentially
const processQueue = async (onError: (item: T_Base) => void) => {
  const nonErroredQ = queue.filter((el) => {
    if (el?._meta) {
      if (el?._meta?.isError === false) return true;
      else return false;
    } else return true;
  });

  if (isProcessing || nonErroredQ.length === 0) return;

  isProcessing = true;
  const currentCall = nonErroredQ[0];

  currentCall["_meta"] = { isProcessing: true };
  try {
    const { _id, _meta, key, ...restBody } = currentCall;
    await api.patch(`/lead/case/${_id}`, restBody);
    queue = queue.filter((el) => el.key !== currentCall.key); // Remove processed call
  } catch (error) {
    const { _meta, ...rest } = currentCall;
    queue = queue.map((el) =>
      el.key === currentCall.key
        ? {
            ...rest,
            _meta: {
              isProcessing: false,
              isError: true,
            },
          }
        : el,
    );
    onError(currentCall);
  }
  notify();
  isProcessing = false;
  processQueue(onError); // Process next item
};

const schema = z.object({
  _id: z.string(),
  email: z.string().email().optional(),
  phone: z
    .string()
    .regex(/^\+1 \d{3}-\d{3}-\d{4}$/)
    .optional(),
});

export function addToQueue(item: T_Base, onError: (item: T_Base) => void) {
  if (item?.email?.length === 0 || item?.phone?.length === 0) return;
  const validItem = schema.safeParse(item);

  const obj: T_D = { ...item };
  if (validItem.error) {
    obj._meta = { isError: true, isProcessing: false };
  }
  queue = [...queue, obj];

  notify();
  processQueue(onError);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return queue;
}
function notify() {
  for (const listener of listeners) {
    listener();
  }
}
export function useApiQueue() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
